import { useEffect, useRef } from "react";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import { isVideoFilename } from "../../../utils";
import { fetchTogetherSessionApi, leaveTogetherApi, syncTogetherApi } from "../../../togetherApi";
import { loadGalleryData } from "../../../galleryApi";
import type { MediaItem, TogetherSession } from "../../../types";
import { isSocketConnected, onSocketOpen, type TogetherSyncData } from "../../../chatSocket";
import { logVideoPlayRejection } from "../../../videoDiagnostics";

// Never sync by raw array index - the peer's appState.media may be sorted
// or filtered differently than this client's, so the only thing safe to
// send/receive over the wire is a filename, resolved locally against
// whatever list is actually loaded here. Returns -1 if not found (the
// caller's job to handle - toast + leave, not silently open the wrong item).
export function resolveMediaIndex(media: MediaItem[], filename: string): number {
  return media.findIndex((m) => m.filename === filename);
}

// Sync throttle: outgoing position/play-state updates while a video is
// playing are capped to this interval - matches the server's own 500ms
// rate-limit backstop with headroom so the client rarely actually hits it.
export const TOGETHER_SYNC_THROTTLE_MS = 2000;
// Mirrors the server's own TOGETHER_SYNC_MIN_INTERVAL_MS (server.js) with
// 100ms headroom - used to schedule the one retry after a discrete
// play/pause/seek sync gets 429'd, so the retry lands just past the
// server's window instead of racing straight back into it.
const TOGETHER_SYNC_MIN_INTERVAL_MS_CLIENT = 600;

// The sharpest risk in this feature: applying an incoming remote sync must
// not itself trigger an outgoing sync (infinite echo between two peers).
// isApplyingRemoteRef is the guard the video event listeners check before
// sending; lastSentFilenameRef is a second, independent guard specifically
// for photo navigation - by the time the nav-sync effect actually re-runs
// (a full render later), isApplyingRemoteRef may already have been reset,
// so the nav effect relies on this dedup instead.
export function useTogetherLiveSync(session: TogetherSession | null, media: MediaItem[], currentMediaIndex: number) {
  const isApplyingRemoteRef = useRef(false);
  const lastSentFilenameRef = useRef<string | null>(null);
  // Distinguishes "the session itself just became active/joined" (the
  // item we're already showing is the session's own starting point, not
  // something to broadcast) from "a real navigation happened within an
  // already-established session." Deliberately self-contained inside this
  // one effect rather than seeded by a caller: an earlier version relied
  // on TogetherRoot's own "auto-open on activation" effect to seed
  // lastSentFilenameRef first, but React runs a component's OWN effects
  // after this hook's (hooks are called first in the component body), so
  // that seed always arrived one render too late - confirmed via a real
  // two-window test where the activation itself sent a redundant sync
  // that then rate-limited (429) the very next real navigation.
  const lastSyncedSessionIdRef = useRef<string | null>(null);
  // Guards the host join-sync effect below to firing once per session.
  const joinSyncSentRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // A discrete play/pause/seek sync that lands inside the server's 500ms
  // rate-limit window (TOGETHER_SYNC_MIN_INTERVAL_MS) gets dropped (429).
  // For ongoing playback that's harmless - the periodic interval covers it
  // a couple seconds later - but pausing stops that interval immediately,
  // so a dropped PAUSE sync has no "next tick" to ever arrive on. This
  // retries once, just past the server's window, re-reading the video's
  // CURRENT state at retry time (not the stale snapshot from the original
  // attempt) so a fast play-pause-play flurry still converges on the truth
  // rather than replaying an outdated intermediate state.
  const retrySyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // CLIENT-003: a peer's synced filename can fail to resolve locally
  // (resolveMediaIndex returns -1) for reasons ranging from "this
  // client's own gallery list just hasn't caught up yet" (recoverable -
  // see the refetch-and-recheck below) to "genuinely not in this
  // client's currently loaded/filtered media" (not recoverable without a
  // bigger redesign - same case TogetherRoot.tsx's own session-start
  // check already leaves the session for). unresolvableHandledRef stores
  // the sessionId already given up on, so a burst of rapid sync events
  // (as often as every 2s during video playback, or a backlog delivered
  // on reconnect) triggers at most one recovery attempt/toast/leave per
  // session instead of repeating for every event. recoveryInFlightRef
  // prevents two overlapping refetch attempts if a second sync event
  // arrives while the first's refetch is still in flight.
  const unresolvableHandledRef = useRef<string | null>(null);
  const recoveryInFlightRef = useRef(false);
  // Guards discoverOrResyncTogetherSession against running twice at once -
  // it can be triggered by several near-simultaneous signals (socket
  // auto-reconnect, the browser `online` event, `visibilitychange`, a
  // manual Rejoin tap), and must be idempotent under all of them.
  const discoveryInFlightRef = useRef(false);
  // Ordering guard for applyRemoteSync: both participants send updates
  // independently (each side runs its own periodic while-playing interval,
  // plus discrete play/pause/seek events), so a message that was already
  // in flight before a MORE RECENT one can still arrive second - without
  // tracking "the newest authoritative timestamp actually applied so far"
  // and discarding anything older, a stale message like that would silently
  // undo a just-applied pause/play. Reset to 0 whenever the session itself
  // changes (see the effect below) so a new session is never blocked by a
  // previous one's bookkeeping.
  const lastAppliedUpdatedAtRef = useRef(0);
  // applyRemoteSync is registered into the socket handler registry once,
  // on mount (see TogetherRoot.tsx) - it must always see the LATEST media
  // list, not whatever was captured in that first render's closure, same
  // "stable function, fresh data via ref" fix useAppState.ts needed.
  const mediaRef = useRef(media);
  mediaRef.current = media;
  // Same "stable function, fresh data via ref" need as mediaRef - applyRemoteSync
  // and resyncAfterReconnect are both registered/closed-over once, so a plain
  // `session` read inside either would see whatever it was at that first
  // render (null, before any session existed), not the current one.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const sessionId = session?.status === "active" ? session.id : null;
  const currentItem = currentMediaIndex >= 0 ? (media[currentMediaIndex] ?? null) : null;
  const currentFilename = currentItem?.filename ?? null;

  // Outgoing: photo navigation syncs immediately (not throttled - it's a
  // discrete, infrequent action, unlike continuous video playback).
  useEffect(() => {
    if (!sessionId || !currentItem) return;
    if (isApplyingRemoteRef.current) return;

    if (lastSyncedSessionIdRef.current !== sessionId) {
      lastSyncedSessionIdRef.current = sessionId;
      lastSentFilenameRef.current = currentItem.filename;
      return;
    }

    if (lastSentFilenameRef.current === currentItem.filename) return;
    lastSentFilenameRef.current = currentItem.filename;
    syncTogetherApi(sessionId, {
      filename: currentItem.filename,
      mediaType: isVideoFilename(currentItem.filename) ? "video" : "image",
      position: 0,
      playing: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, currentFilename]);

  useEffect(() => {
    lastAppliedUpdatedAtRef.current = 0;
  }, [sessionId]);

  // Outgoing, host only: the moment a session goes active, push the host's
  // ACTUAL current filename/position/playing once. The nav-sync effect
  // above deliberately treats "session just activated" as a no-op (see its
  // comment) to avoid re-broadcasting stale position:0 - but that leaves a
  // real gap: if the host is mid-video and paused when the guest joins, no
  // periodic interval is running (that only starts on play) and no
  // play/pause/seek event is coming, so the guest never learns where the
  // host actually is. Guest-side, this is a harmless no-op: currentItem is
  // still null on the guest's first active render (before the "auto-open
  // Lightbox" effect in TogetherRoot.tsx navigates them), so the effect
  // just returns.
  useEffect(() => {
    if (!sessionId || !session?.isHost || !currentItem) return;
    if (joinSyncSentRef.current === sessionId) return;
    joinSyncSentRef.current = sessionId;

    const isVideo = isVideoFilename(currentItem.filename);
    const video = isVideo ? (document.getElementById("mediaModalVideo") as HTMLVideoElement | null) : null;
    syncTogetherApi(sessionId, {
      filename: currentItem.filename,
      mediaType: isVideo ? "video" : "image",
      position: video ? video.currentTime : 0,
      playing: video ? !video.paused : false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, session?.isHost, currentFilename]);

  // Outgoing: video play/pause/seeked fire an immediate sync; a 2s
  // interval covers ongoing playback position drift in between.
  useEffect(() => {
    if (!sessionId || !currentItem || !isVideoFilename(currentItem.filename)) return;
    const video = document.getElementById("mediaModalVideo") as HTMLVideoElement | null;
    if (!video) return;
    const filename = currentItem.filename;

    function sendNow(): void {
      if (isApplyingRemoteRef.current || !video) return;
      if (retrySyncTimerRef.current) {
        clearTimeout(retrySyncTimerRef.current);
        retrySyncTimerRef.current = null;
      }
      syncTogetherApi(sessionId!, { filename, mediaType: "video", position: video.currentTime, playing: !video.paused }).then((result) => {
        // Only retry a rate-limit drop, not a real failure (session ended,
        // forbidden, offline) - those aren't worth re-attempting and a
        // stale closure retrying into a dead session would be pointless at
        // best. sendNow() itself re-reads video.currentTime/paused fresh
        // at retry time, so this converges on whatever's true THEN, not a
        // now-outdated snapshot from the original attempt.
        if (!result.ok && result.rateLimited) {
          retrySyncTimerRef.current = setTimeout(sendNow, TOGETHER_SYNC_MIN_INTERVAL_MS_CLIENT);
        }
      });
    }
    function startInterval(): void {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(sendNow, TOGETHER_SYNC_THROTTLE_MS);
    }
    function stopInterval(): void {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    function onPlay(): void {
      // Any real "play" on this element - whether it started on its own or
      // via the manual "Tap to resume in sync" affordance TogetherRoot.tsx
      // shows after a blocked remote play() - means playback is no longer
      // stuck, so the affordance (if showing) can go away.
      if (appState.togetherPlaybackBlocked) {
        appState.togetherPlaybackBlocked = false;
        notifyStateChange();
      }
      sendNow();
      startInterval();
    }
    function onPause(): void {
      sendNow();
      stopInterval();
    }
    function onSeeked(): void {
      sendNow();
    }

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("seeked", onSeeked);
    if (!video.paused) startInterval();

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("seeked", onSeeked);
      stopInterval();
      // A pending retry (see sendNow's rate-limit handling) belongs to
      // THIS filename/video instance - if the item changed before it
      // fired, letting it go would resend a now-irrelevant, possibly
      // wrong-filename sync.
      if (retrySyncTimerRef.current) {
        clearTimeout(retrySyncTimerRef.current);
        retrySyncTimerRef.current = null;
      }
    };
  }, [sessionId, currentFilename]);

  // Incoming: apply a peer's sync. Video manipulation is deferred one
  // frame since a filename change may not have mounted the new <video>
  // element yet; the isApplyingRemote flag stays set a little past that to
  // cover the play()/pause()/currentTime= calls' own event firing.
  async function applyRemoteSync(data: TogetherSyncData): Promise<void> {
    if (unresolvableHandledRef.current === data.sessionId) return;

    // Out-of-order guard: both sides send updates independently (each
    // runs its own periodic while-playing interval on top of discrete
    // play/pause/seek events), so a message already in flight before a
    // MORE RECENT one can still arrive second. Without this, a stale
    // "still playing" tick that was en route before a just-sent pause
    // could land right after it and silently un-pause the video again -
    // confirmed as the actual cause of "the other person's pause doesn't
    // stick" reports. Only ever moves forward; a message with an equal or
    // older timestamp than the newest one already applied is dropped.
    const incomingAt = Date.parse(data.updatedAt);
    if (Number.isFinite(incomingAt)) {
      if (incomingAt < lastAppliedUpdatedAtRef.current) return;
      lastAppliedUpdatedAtRef.current = incomingAt;
    }

    isApplyingRemoteRef.current = true;

    let idx = resolveMediaIndex(mediaRef.current, data.filename);

    if (idx === -1) {
      if (recoveryInFlightRef.current) {
        // A different sync event's recovery attempt is already in
        // flight for this same gap - let it finish rather than racing a
        // second, redundant refetch.
        isApplyingRemoteRef.current = false;
        return;
      }
      recoveryInFlightRef.current = true;
      try {
        // The common real-world cause isn't "this item doesn't exist" -
        // it's this client's own gallery list not having caught up yet
        // (a fresh load/reconnect racing the initial fetch, or a
        // just-added item whose media_changed-triggered refetch hasn't
        // landed). One refetch-and-recheck recovers those without
        // ending the session.
        await loadGalleryData();
        idx = resolveMediaIndex(mediaRef.current, data.filename);
      } finally {
        recoveryInFlightRef.current = false;
      }
    }

    if (idx === -1) {
      // Still not found after a refetch - genuinely not something this
      // client can follow (deleted, or simply not in this client's
      // current album/filter context). Leaving the client "synced" with
      // nothing actually updating would be worse than a clear exit -
      // same toast+leave pattern as TogetherRoot.tsx's own session-start
      // check for the identical condition. unresolvableHandledRef stops
      // this from repeating for every further sync event this session.
      unresolvableHandledRef.current = data.sessionId;
      isApplyingRemoteRef.current = false;
      showToast("Can't find what your partner is watching - leaving Watch Together.", "error");
      leaveTogetherApi(data.sessionId);
      return;
    }

    // appState.currentMediaIndex >= 0 guard: only reposition an ALREADY-OPEN
    // Lightbox. Without this, applying a sync (including one discovered
    // passively - see discoverOrResyncTogetherSession below) would force-open
    // the Lightbox from any screen, which is exactly the "auto-hijacking
    // navigation" a passive discovery must never do. When the Lightbox is
    // closed, the video-element block below is already a safe no-op anyway
    // (getElementById returns null), so this one condition is sufficient.
    if (idx !== appState.currentMediaIndex && appState.currentMediaIndex >= 0) {
      lastSentFilenameRef.current = data.filename;
      appState.currentMediaIndex = idx;
      notifyStateChange();
    }

    if (data.mediaType === "video") {
      requestAnimationFrame(() => {
        const video = document.getElementById("mediaModalVideo") as HTMLVideoElement | null;
        if (video) {
          if (Math.abs(video.currentTime - data.position) > 0.5) video.currentTime = data.position;
          if (data.playing && video.paused) {
            video.play()?.catch((err) => {
              logVideoPlayRejection("watch-together-sync", video, data.filename, err);
              // Video playback audit, Bug A: a NotAllowedError specifically
              // means Safari/WebKit's user-activation gate rejected this
              // call because there was no qualifying gesture in its call
              // stack (this ran from a WebSocket message handler) - that's
              // a PERMISSION rejection, not a readiness one, and retrying
              // the exact same no-gesture play() (the canplay retry below)
              // cannot recover it; it would just fail again the same way.
              // Surface a real tappable affordance instead - see
              // TogetherRoot.tsx's togetherPlaybackBlocked-driven overlay,
              // cleared by onPlay above once the tap actually succeeds.
              if (err instanceof Error && err.name === "NotAllowedError") {
                appState.togetherPlaybackBlocked = true;
                notifyStateChange();
                return;
              }
              // Any other rejection reason (most commonly: readyState too
              // low right after a filename/currentTime change) genuinely IS
              // readiness-based, so retrying once the browser reports it's
              // actually able to play is the right recovery here.
              showToast(
                sessionRef.current?.peerName ? `Catching up with ${sessionRef.current.peerName}'s video…` : "Catching up…",
                "info",
                4000,
              );
              const retry = () => {
                video.removeEventListener("canplay", retry);
                if (video.paused) {
                  video.play()?.catch((retryErr) => logVideoPlayRejection("watch-together-sync-retry", video, data.filename, retryErr));
                }
              };
              video.addEventListener("canplay", retry, { once: true });
            });
          } else if (!data.playing && !video.paused) {
            video.pause();
          }
        }
        setTimeout(() => {
          isApplyingRemoteRef.current = false;
        }, 50);
      });
    } else {
      setTimeout(() => {
        isApplyingRemoteRef.current = false;
      }, 50);
    }
  }

  // together_* events are push-only over the WS - anything that happened
  // while this socket was down (background/backoff/flaky network, up to
  // the 30s reconnect cap) is otherwise silently missed rather than just
  // delayed: a session that ended while offline would leave "Watching
  // with X" showing forever, and a session that moved on would leave this
  // side stuck on a stale filename/position with no periodic correction
  // (that only runs while video is actively playing). Also doubles as
  // fresh-boot DISCOVERY: unlike the old resyncAfterReconnect this
  // replaces, this does NOT early-return when appState.togetherSession is
  // null - a full page load starts with exactly that null state, which is
  // why a browser refresh never used to discover a still-active session at
  // all even though this runs on the very first socket connect too. When
  // nothing was known locally and the server has an active session, this
  // sets togetherAwaitingRejoin instead of calling applyRemoteSync, so
  // discovery itself never force-navigates - TogetherRejoinPill offers an
  // explicit tap instead (see TogetherRoot.tsx).
  async function discoverOrResyncTogetherSession(): Promise<void> {
    if (discoveryInFlightRef.current) return;
    discoveryInFlightRef.current = true;
    try {
      const knownBefore = !!appState.togetherSession;
      const fresh = await fetchTogetherSessionApi();

      if (!fresh) {
        if (knownBefore) {
          showToast("Your watch-together session ended while you were disconnected.", "info");
          appState.togetherSession = null;
          appState.togetherAwaitingRejoin = false;
          notifyStateChange();
        }
        return;
      }

      appState.togetherSession = fresh;
      if (!knownBefore) {
        appState.togetherAwaitingRejoin = fresh.status === "active";
        notifyStateChange();
        return;
      }
      notifyStateChange();
      if (fresh.status === "active") {
        applyRemoteSync({
          sessionId: fresh.id,
          filename: fresh.filename,
          mediaType: fresh.mediaType,
          position: fresh.position,
          playing: fresh.playing,
          fromUserId: fresh.peerUserId,
          updatedAt: fresh.updatedAt,
        });
      }
    } finally {
      discoveryInFlightRef.current = false;
    }
  }

  useEffect(() => {
    return onSocketOpen(() => {
      discoverOrResyncTogetherSession();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Two more discovery triggers, both gated on the socket NOT currently
  // being connected. Without that guard this fired on every ordinary tab
  // switch/focus change too, even with a perfectly healthy socket - and
  // since discoverOrResyncTogetherSession's GET can resolve with a
  // slightly-stale snapshot (a race against a together_sync WS push that
  // landed a moment earlier, e.g. a partner's pause), applying it would
  // occasionally stomp a just-applied live update back to the old
  // playing/position - confirmed as the actual cause of a real "pause
  // doesn't stick for the other person" report, most reproducible testing
  // with two tabs on one machine (constant visibilitychange firing).
  // Restricting to the disconnected case keeps the real value (recovering
  // a tab that was fully backgrounded/suspended, where no close event
  // fired but the connection is genuinely gone) without racing the common,
  // perfectly-fine case where the WS is already delivering live updates.
  useEffect(() => {
    function onOnline(): void {
      if (!isSocketConnected()) discoverOrResyncTogetherSession();
    }
    function onVisibilityChange(): void {
      if (document.visibilityState === "visible" && !isSocketConnected()) discoverOrResyncTogetherSession();
    }
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { applyRemoteSync, discoverOrResyncTogetherSession };
}
